import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ContactNote {
  id: string;
  contactId: string;
  category: string;
  content: string;
  createdById: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Reminder {
  id: string;
  entityType: 'contact' | 'note';
  entityId: string;
  assigneeId: string;
  assigneeName?: string;
  dueAt: string;
  title: string;
  description?: string;
  isCompleted: boolean;
  completedAt?: string;
  createdAt: string;
}

export interface CreateNotePayload {
  category: string;
  content: string;
  reminderDate?: string;
  reminderAssigneeId?: string;
}

export interface CreateReminderPayload {
  title: string;
  description?: string;
  dueAt: string;
  assigneeId: string;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const notesRemindersKeys = {
  all: ['contact-notes-reminders'] as const,
  notes: (contactId: string) => [...notesRemindersKeys.all, 'notes', contactId] as const,
  reminders: (contactId: string) => [...notesRemindersKeys.all, 'reminders', contactId] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch notes for a contact.
 */
export function useContactNotes(contactId: string) {
  return useQuery({
    queryKey: notesRemindersKeys.notes(contactId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Array<Record<string, unknown>>>>(
        `/contacts/${contactId}/notes`,
      );
      const raw = response.data.data ?? [];
      return raw.map(mapNote);
    },
    enabled: !!contactId,
  });
}

/**
 * Fetch reminders for a contact.
 */
export function useContactReminders(contactId: string) {
  return useQuery({
    queryKey: notesRemindersKeys.reminders(contactId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Array<Record<string, unknown>>>>(
        `/contacts/${contactId}/reminders`,
      );
      const raw = response.data.data ?? [];
      return raw.map(mapReminder);
    },
    enabled: !!contactId,
  });
}

/**
 * Create a new note on a contact.
 */
export function useCreateNote(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateNotePayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/contacts/${contactId}/notes`,
        {
          category: payload.category,
          content: payload.content,
          reminder_date: payload.reminderDate || null,
          reminder_assignee_id: payload.reminderAssigneeId || null,
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesRemindersKeys.notes(contactId) });
      queryClient.invalidateQueries({ queryKey: notesRemindersKeys.reminders(contactId) });
    },
  });
}

/**
 * Create a new reminder on a contact.
 */
export function useCreateReminder(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateReminderPayload) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/contacts/${contactId}/reminders`,
        {
          title: payload.title,
          description: payload.description || null,
          due_at: payload.dueAt,
          assignee_id: payload.assigneeId,
        },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notesRemindersKeys.reminders(contactId),
      });
    },
  });
}

/**
 * Mark a reminder as completed.
 */
export function useCompleteReminder(contactId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reminderId: string) => {
      const response = await apiClient.patch<ApiResponse<Record<string, unknown>>>(
        `/contacts/reminders/${reminderId}`,
        { is_completed: true },
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notesRemindersKeys.reminders(contactId),
      });
    },
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapNote(n: Record<string, unknown>): ContactNote {
  return {
    id: n.id as string,
    contactId: (n.contact_id as string) ?? (n.contactId as string) ?? '',
    category: (n.category as string) ?? '',
    content: (n.content as string) ?? '',
    createdById: (n.created_by_id as string) ?? (n.createdById as string) ?? '',
    createdByName: (n.created_by_name as string) ?? (n.createdByName as string) ?? undefined,
    createdAt: (n.created_at as string) ?? (n.createdAt as string) ?? '',
    updatedAt: (n.updated_at as string) ?? (n.updatedAt as string) ?? '',
  };
}

function mapReminder(r: Record<string, unknown>): Reminder {
  return {
    id: r.id as string,
    entityType:
      (r.entity_type as 'contact' | 'note') ?? (r.entityType as 'contact' | 'note') ?? 'contact',
    entityId: (r.entity_id as string) ?? (r.entityId as string) ?? '',
    assigneeId: (r.assignee_id as string) ?? (r.assigneeId as string) ?? '',
    assigneeName: (r.assignee_name as string) ?? (r.assigneeName as string) ?? undefined,
    dueAt: (r.due_at as string) ?? (r.dueAt as string) ?? '',
    title: (r.title as string) ?? '',
    description: (r.description as string) ?? undefined,
    isCompleted: (r.is_completed as boolean) ?? (r.isCompleted as boolean) ?? false,
    completedAt: (r.completed_at as string) ?? (r.completedAt as string) ?? undefined,
    createdAt: (r.created_at as string) ?? (r.createdAt as string) ?? '',
  };
}
